import {useCallback, useEffect, useMemo, useState} from 'react';
import {Box, Button, Skeleton, Stack, Typography} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import {SplitTabsPanel} from '@/components/common';
import {
    AuditProgramSectionVO,
    getVSectionVO,
    saveVSectionPage,
} from '@/services/auditService/auditProgramService';
import {
    V1_PAGE,
    V2_PAGE,
    V2_1_PAGE,
    V3_PAGE,
    V4_1_PAGE,
    V_PAGE_TITLES,
    V_SECTION_PAGES,
    V2Page,
    V3Page,
    VJournalPage,
    VProgrammePage,
    VSectionPageCode,
} from '@/pages/components/audit-program/v-section/constants';
import V2JournalWpView from '@/pages/components/audit-program/v-section/V2JournalWpView';
import VJournalTestView from '@/pages/components/audit-program/v-section/VJournalTestView';
import V3EstimatesView from '@/pages/components/audit-program/v-section/V3EstimatesView';
import G0ProgrammeView from '@/pages/components/audit-program/g-section/G0ProgrammeView';
import PageTabLabel from '@/pages/components/audit-program/PageTabLabel';
import {useProgramSectionTemplates} from '@/pages/components/audit-program/templates/useProgramSectionTemplates';

export type VSectionViewProps = {
    companyId: number;
    annualId: number;
    companyName?: string | null;
    initialPage?: VSectionPageCode;
};

function toStringRecord(data?: Record<string, unknown>): Record<string, string> {
    if (!data) return {};
    return Object.fromEntries(
        Object.entries(data).map(([k, v]) => [k, v == null ? '' : String(v)]),
    );
}

function withHeaderDefaults(
    page: Record<string, string>,
    data: AuditProgramSectionVO,
    refCode: string,
): Record<string, string> {
    return {
        ...page,
        client: page.client || data.client || '',
        yearEnd: page.yearEnd || data.yearEnd || '',
        fileNo: page.fileNo || data.fileNo || '',
        preparedBy: page.preparedBy || data.preparedInitial || data.preparedBy || '',
        completedBy: page.completedBy || data.preparedInitial || data.preparedBy || '',
        reviewedBy: page.reviewedBy || data.reviewedInitial || data.reviewedBy || '',
        ref: page.ref || refCode,
    };
}

export default function VSectionView({
    companyId,
    annualId,
    companyName,
    initialPage = 'V1',
}: VSectionViewProps) {
    const [pageKey, setPageKey] = useState<VSectionPageCode>(initialPage);
    const [header, setHeader] = useState<AuditProgramSectionVO | null>(null);
    const [pageData, setPageData] = useState<Record<string, Record<string, string>>>({});
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [dirty, setDirty] = useState<Record<string, boolean>>({});
    const {getContent} = useProgramSectionTemplates('V');

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await getVSectionVO(companyId, annualId);
            if (companyName && !data.client) {
                data.client = companyName;
            }
            setHeader(data);
            const pages: Record<string, Record<string, string>> = {};
            for (const code of V_SECTION_PAGES) {
                pages[code] = withHeaderDefaults(toStringRecord(data.pages?.[code]), data, code);
            }
            setPageData(pages);
            setDirty({});
        } catch (e: unknown) {
            const msg = e instanceof Error ? e.message : 'Failed to load V Section';
            setError(msg);
            setHeader(null);
        } finally {
            setLoading(false);
        }
    }, [companyId, annualId, companyName]);

    useEffect(() => {
        void load();
    }, [load]);

    useEffect(() => {
        setPageKey(initialPage);
    }, [initialPage]);

    const handleFieldChange = useCallback((pageCode: VSectionPageCode, key: string, value: string) => {
        setPageData((prev) => ({
            ...prev,
            [pageCode]: {...(prev[pageCode] ?? {}), [key]: value},
        }));
        setDirty((prev) => ({...prev, [pageCode]: true}));
    }, []);

    const handleSave = useCallback(async () => {
        if (!dirty[pageKey]) return;
        setSaving(true);
        setError(null);
        try {
            await saveVSectionPage(companyId, annualId, pageKey, pageData[pageKey] ?? {});
            setDirty((prev) => ({...prev, [pageKey]: false}));
        } catch (e: unknown) {
            const msg = e instanceof Error ? e.message : 'Failed to save';
            setError(msg);
        } finally {
            setSaving(false);
        }
    }, [companyId, annualId, dirty, pageKey, pageData]);

    const v1Content = (getContent('V1') ?? V1_PAGE) as VProgrammePage;
    const v2Content = (getContent('V2') ?? V2_PAGE) as V2Page;
    const v21Content = (getContent('V2.1') ?? V2_1_PAGE) as VJournalPage;
    const v3Content = (getContent('V3') ?? V3_PAGE) as V3Page;
    const v41Content = (getContent('V4.1') ?? V4_1_PAGE) as VJournalPage;

    const tabs = useMemo(() => V_SECTION_PAGES.map((code) => ({
        key: code,
        label: <PageTabLabel code={code} title={V_PAGE_TITLES[code]}/>,
        icon: code,
        content: header ? (
            code === 'V1' ? (
                <G0ProgrammeView
                    header={header}
                    page={v1Content}
                    refCode="V1"
                    values={pageData.V1 ?? {}}
                    onChange={(k, v) => handleFieldChange('V1', k, v)}
                />
            ) : code === 'V2' ? (
                <V2JournalWpView
                    header={header}
                    page={v2Content}
                    values={pageData.V2 ?? {}}
                    onChange={(k, v) => handleFieldChange('V2', k, v)}
                />
            ) : code === 'V2.1' ? (
                <VJournalTestView
                    header={header}
                    page={v21Content}
                    refCode="V2.1"
                    values={pageData['V2.1'] ?? {}}
                    onChange={(k, v) => handleFieldChange('V2.1', k, v)}
                />
            ) : code === 'V3' ? (
                <V3EstimatesView
                    header={header}
                    page={v3Content}
                    values={pageData.V3 ?? {}}
                    onChange={(k, v) => handleFieldChange('V3', k, v)}
                />
            ) : (
                <VJournalTestView
                    header={header}
                    page={v41Content}
                    refCode="V4.1"
                    values={pageData['V4.1'] ?? {}}
                    onChange={(k, v) => handleFieldChange('V4.1', k, v)}
                />
            )
        ) : null,
    })), [header, pageData, handleFieldChange, v1Content, v2Content, v21Content, v3Content, v41Content]);

    if (loading) {
        return <Skeleton variant="rectangular" height={480} sx={{m: 2}}/>;
    }

    if (error && !header) {
        return (
            <Box sx={{p: 2}}>
                <Typography color="error">{error}</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column'}}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{px: 2, pt: 1, pb: 0.5}}>
                <Typography variant="subtitle1" fontWeight={600}>
                    V Section — General ledger
                </Typography>
                <Button
                    size="small"
                    variant="contained"
                    startIcon={<SaveIcon/>}
                    disabled={!dirty[pageKey] || saving}
                    onClick={() => void handleSave()}
                >
                    {saving ? 'Saving…' : 'Save'}
                </Button>
            </Stack>
            {error ? (
                <Typography color="error" variant="body2" sx={{px: 2}}>{error}</Typography>
            ) : null}
            <Box sx={{flex: 1, minHeight: 0}}>
                <SplitTabsPanel
                    tabs={tabs}
                    selectedKey={pageKey}
                    onChange={(k) => setPageKey(k as VSectionPageCode)}
                    height="100%"
                    allowMaximize={false}
                    contentWrapperSx={{height: '100%', overflow: 'auto', p: 0}}
                    rightPaneSx={{height: '100%', overflow: 'hidden'}}
                />
            </Box>
        </Box>
    );
}
